import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Hero from '../common/Hero';
import Footer from '../common/Footer';

const DonationSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasVerified, setHasVerified] = useState(false);
  const [error, setError] = useState(null);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptMessage, setReceiptMessage] = useState('');

  const apiBaseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const verifyPayment = async () => {
      if (hasVerified) return;
      setHasVerified(true);

      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setError('No session ID found in URL');
        setLoading(false);
        setHasVerified(true);
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/paymentall/confirm-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(process.env.REACT_APP_NAME + ':' + process.env.REACT_APP_PASS),
          },
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await response.json();

        // ── 409: already processed → still a SUCCESS, show donor ──────────
        if (response.status === 409) {
          if (data.donor) {
            setDonation(normaliseDonor(data.donor));
            setError(null); // clear any error — payment was already confirmed
          } else {
            // 409 with no donor body is unusual — surface a friendly message
            setError('This payment was already processed. If you need help, contact support.');
          }
          return;
        }

        if (!response.ok) {
          throw new Error(data.error || 'Payment verification failed');
        }

        if (data.success && data.donor) {
          setDonation(normaliseDonor(data.donor));
          setError(null);
        } else {
          throw new Error(data.error || 'Payment verification failed');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setDonation(prev => {
          if (prev) return prev; // keep existing data, don't show error
          setError(
            err.message ||
            'We received your payment but encountered an issue confirming it. Please contact support.'
          );
          return null;
        });

      } finally {
        setHasVerified(true);
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location.search]);

  const normaliseDonor = (donor) => ({
    ...donor,
    paymentMethod: formatPaymentMethod(donor.paymentMethod),
    amount: typeof donor.amount === 'number'
      ? donor.amount.toFixed(2)
      : parseFloat(donor.amount || 0).toFixed(2),
    date: donor.paymentDate
      ? new Date(donor.paymentDate).toLocaleString()
      : new Date().toLocaleString(),
    transactionId: donor.paymentRefId || donor._id || 'N/A',
  });

  const formatPaymentMethod = (method) => {
    if (!method) return 'Credit Card';
    return method.charAt(0).toUpperCase() + method.slice(1).replace(/_/g, ' ');
  };

  // ── Send receipt ──────────────────────────────────────────────────────────
  const handleSendReceipt = async (emailOverride) => {
    setSendingReceipt(true);
    setEmailError('');

    try {
      const body = { session_id: donation.transactionId };
      if (emailOverride) body.email = emailOverride;

      const res = await fetch(`${apiBaseUrl}/paymentall/send-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(process.env.REACT_APP_NAME + ':' + process.env.REACT_APP_PASS),
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      // Backend says guest needs to supply an email
      if (data.requiresEmail) {
        setShowEmailModal(true);
        setSendingReceipt(false);
        return;
      }

      if (data.status === 1) {
        setReceiptSent(true);
        setReceiptMessage(data.message || 'Receipt sent successfully!');
        setShowEmailModal(false);
        setGuestEmail('');
      } else {
        setEmailError(data.error || 'Failed to send receipt. Please try again.');
      }
    } catch (err) {
      setEmailError('Network error. Please try again.');
    } finally {
      setSendingReceipt(false);
    }
  };

  const handleGuestEmailSubmit = (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!guestEmail.trim()) {
      setEmailError('Please enter your email address.');
      return;
    }
    if (!emailRegex.test(guestEmail.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    handleSendReceipt(guestEmail.trim());
  };

  const closeModal = () => {
    setShowEmailModal(false);
    setGuestEmail('');
    setEmailError('');
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '60vh', gap: '16px' }}>
        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
          <span className="visually-hidden">Verifying payment...</span>
        </div>
        <p className="text-muted fw-semibold">Verifying your payment, please wait...</p>
      </div>
    );
  }

  const isSuccess = !error && donation;

  return (
    <>
      <Hero
        title={isSuccess ? 'Donation Successful' : 'Donation Processing'}
        breadcrumbs={[
          { text: 'Home', link: '/' },
          { text: 'Donate', link: '/donate' },
          { text: isSuccess ? 'Success' : 'Processing' },
        ]}
      />

      <div className="container-fluid py-5">
        <div className="container donation-status wow fadeIn" data-wow-delay="0.1s">
          <div className="status-content">

            {/* ── Status icon ── */}
            <div className="status-icon">
              <i className={`fas ${isSuccess
                ? 'fa-check-circle text-success'
                : 'fa-exclamation-triangle text-warning'}`}
              />
            </div>

            {/* ── Heading ── */}
            {isSuccess ? (
              <>
                <h2 className="mb-3">Thank You For Your Donation!</h2>
                <p className="mb-4">
                  Your generous contribution will help support our masjid and its programs.
                  May Allah reward you for your kindness.
                </p>
              </>
            ) : (
              <>
                <h2 className="mb-3">Donation Processing Issue</h2>
                <div className="alert alert-warning mb-4">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              </>
            )}

            {/* ── Donation details ── */}
            {donation && (
              <div className="donation-details">
                <div className="detail-item">
                  <span>Transaction ID:</span>
                  <strong style={{ wordBreak: 'break-all', fontSize: '13px' }}>
                    {donation.transactionId}
                  </strong>
                </div>
                <div className="detail-item">
                  <span>Category:</span>
                  <strong>{donation.category || 'N/A'}</strong>
                </div>
                <div className="detail-item">
                  <span>Amount:</span>
                  <strong className="text-success">${donation.amount}</strong>
                </div>
                <div className="detail-item">
                  <span>Payment Method:</span>
                  <strong>{donation.paymentMethod}</strong>
                </div>
                <div className="detail-item">
                  <span>Date:</span>
                  <strong>{donation.date}</strong>
                </div>
              </div>
            )}

            {/* ── Receipt sent confirmation ── */}
            {receiptSent && (
              <div className="alert alert-success mt-3 d-flex align-items-center gap-2">
                <i className="fas fa-envelope-open-text fa-lg"></i>
                <span>{receiptMessage || 'Receipt has been sent to your email successfully!'}</span>
              </div>
            )}

            {/* ── Action buttons ── */}
            <div className="status-buttons mt-4 d-flex flex-wrap gap-3 justify-content-center">

              {/* Stripe receipt link */}
              {donation?.receiptUrl && (
                <a
                  href={donation.receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary px-4 py-2"
                >
                  <i className="fas fa-receipt me-2"></i> View Receipt
                </a>
              )}

              {/* Email receipt — hidden once sent */}
              {donation && !receiptSent && (
                <button
                  className="btn btn-outline-primary px-4 py-2"
                  onClick={() => handleSendReceipt()}
                  disabled={sendingReceipt}
                >
                  {sendingReceipt ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-envelope me-2" />
                      Email Receipt
                    </>
                  )}
                </button>
              )}

              {/* Donate again */}
              {isSuccess && (
                <button
                  className="btn btn-outline-success px-4 py-2"
                  onClick={() => navigate('/donate')}
                >
                  <i className="fas fa-hand-holding-usd me-2"></i> Donate Again
                </button>
              )}

              {/* Return home */}
              <button
                className="btn btn-outline-secondary px-4 py-2"
                onClick={() => navigate('/')}
              >
                <i className="fas fa-home me-2"></i> Return Home
              </button>

              {/* Retry on error */}
              {error && (
                <button
                  className="btn btn-warning px-4 py-2"
                  onClick={() => { setHasVerified(false); setLoading(true); setError(null); }}
                >
                  <i className="fas fa-sync-alt me-2"></i> Retry Verification
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── Guest email modal ─────────────────────────────────────────────── */}
      {showEmailModal && (
        <div
          className="modal d-block"
          style={{ background: 'rgba(0,0,0,0.6)', zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">

              <div className="modal-header border-0 pb-0">
                <div className="d-flex align-items-center gap-2">
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '50%',
                    background: 'rgba(13,110,253,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <i className="fas fa-envelope text-primary"></i>
                  </div>
                  <h5 className="modal-title mb-0 fw-bold">Email Your Receipt</h5>
                </div>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>

              <form onSubmit={handleGuestEmailSubmit}>
                <div className="modal-body pt-3">
                  <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                    Enter your email address and we'll send your donation receipt right away.
                    Your email is only used to deliver this receipt.
                  </p>

                  <div className="mb-3">
                    <label htmlFor="guestEmail" className="form-label fw-semibold">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fas fa-at text-muted"></i>
                      </span>
                      <input
                        type="email"
                        className={`form-control border-start-0 ps-0 ${emailError ? 'is-invalid' : ''}`}
                        id="guestEmail"
                        placeholder="you@example.com"
                        value={guestEmail}
                        onChange={(e) => { setGuestEmail(e.target.value); setEmailError(''); }}
                        autoFocus
                      />
                      {emailError && (
                        <div className="invalid-feedback">{emailError}</div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 p-2 rounded"
                    style={{ background: 'rgba(13,110,253,0.05)', border: '1px solid rgba(13,110,253,0.1)' }}>
                    <i className="fas fa-shield-alt text-primary" style={{ fontSize: '13px' }}></i>
                    <p className="small text-muted mb-0" style={{ fontSize: '12px' }}>
                      We respect your privacy. Your email is not stored after sending the receipt.
                    </p>
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0 gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={sendingReceipt}
                  >
                    {sendingReceipt ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2" />
                        Send Receipt
                      </>
                    )}
                  </button>
                </div>
              </form>

            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default DonationSuccess;